import { ArrowRight, Bike, ChefHat, Play, TicketPercent } from "lucide-react";
import { useRef, useState } from "react";
import bannerImg from "../../assets/bannerImg.jpg";
import { SITE } from "../../data/site";
import { gsap, MOTION_OK, useGSAP } from "../../lib/gsap";
import { BrushStrokes } from "../illustrations/BrushStrokes";
import { FreshSeal } from "../illustrations/FreshSeal";
import { Button } from "../ui/Button";
import { VideoModal } from "./VideoModal";

const BADGES = [
  { icon: Bike, title: "Entrega rápida", caption: "30 a 50 min" },
  { icon: ChefHat, title: "Qualidade", caption: "Ingredientes frescos" },
  { icon: TicketPercent, title: "Ofertas", caption: "Entrega grátis" },
];

export const Hero = () => {
  const ref = useRef<HTMLElement>(null);
  const [videoOpen, setVideoOpen] = useState(false);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
        tl.from("[data-hero='item']", { y: 40, autoAlpha: 0, duration: 0.8, stagger: 0.09 })
          .from("[data-brush]", { scaleX: 0, transformOrigin: "0% 50%", duration: 0.9, stagger: 0.12, ease: "power2.inOut" }, 0.15)
          .from("[data-hero='image']", { scale: 0.88, autoAlpha: 0, duration: 1.1, ease: "back.out(1.4)" }, 0.25)
          .from("[data-hero='seal']", { scale: 0, rotate: -120, autoAlpha: 0, duration: 0.8, ease: "back.out(2)" }, 0.75)
          .from("[data-hero='note']", { y: 12, rotate: -16, autoAlpha: 0, duration: 0.7 }, 0.95);

        // Leve flutuação contínua do produto.
        gsap.to("[data-hero='float']", { y: -14, duration: 3, ease: "sine.inOut", yoyo: true, repeat: -1 });
      });
    },
    { scope: ref },
  );

  return (
    <section
      id="Hero"
      ref={ref}
      aria-labelledby="hero-title"
      className="relative isolate overflow-hidden bg-night pb-28 pt-28 sm:pt-32 lg:pb-36 lg:pt-40"
    >
      <div aria-hidden="true" className="absolute inset-0 -z-10">
        <div className="bg-dots absolute inset-0 [mask-image:radial-gradient(ellipse_at_30%_40%,black,transparent_70%)]" />
        <div className="absolute -right-48 top-0 size-[680px] rounded-full bg-primary/10 blur-[130px]" />
        <div className="absolute -left-40 bottom-0 size-[420px] rounded-full bg-primary/5 blur-[110px]" />
      </div>

      <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-[1.05fr_1fr] lg:gap-10 lg:px-8">
        <div className="text-center lg:text-left">
          <p data-hero="item" className="font-script text-2xl text-primary sm:text-3xl">
            Bem-vindo à base secreta
          </p>
          <h1
            id="hero-title"
            className="mt-1 font-display text-[clamp(4.75rem,15vw,9.5rem)] leading-[0.85] tracking-wide"
          >
            <span data-hero="item" className="block text-white">
              Ninja
            </span>
            <span data-hero="item" className="block text-primary">
              Burger
            </span>
          </h1>
          <p
            data-hero="item"
            className="mt-5 text-[11px] font-medium uppercase tracking-[0.28em] text-white/85 sm:text-xs"
          >
            Blend artesanal <span className="mx-2 text-primary">|</span> Pão brioche{" "}
            <span className="mx-2 text-primary">|</span> Molhos autorais
          </p>
          <p data-hero="item" className="mx-auto mt-6 max-w-lg text-base leading-relaxed text-muted lg:mx-0">
            {SITE.tagline} Blend preparado diariamente, pão brioche selado na chapa e molhos autorais — com a
            rapidez de um mestre das sombras.
          </p>

          <div data-hero="item" className="mt-9 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
            <Button to="/cardapio" size="lg" icon={<ArrowRight className="size-5" aria-hidden="true" />}>
              Pedir agora
            </Button>
            {SITE.heroVideoUrl && (
              <Button
                variant="ghost"
                size="lg"
                onClick={() => setVideoOpen(true)}
                iconPosition="left"
                icon={
                  <span className="grid size-10 place-items-center rounded-full border border-primary/50 text-primary">
                    <Play className="size-4 fill-current" aria-hidden="true" />
                  </span>
                }
                className="px-3"
              >
                Ver vídeo
              </Button>
            )}
          </div>

          <ul
            data-hero="item"
            className="mx-auto mt-12 grid max-w-lg grid-cols-3 divide-x divide-line lg:mx-0"
          >
            {BADGES.map(({ icon: Icon, title, caption }) => (
              <li key={title} className="flex flex-col items-center gap-2 px-2 text-center sm:px-4 lg:items-start lg:text-left lg:first:pl-0">
                <span className="grid size-11 place-items-center rounded-full border border-primary/30 bg-primary-soft text-primary">
                  <Icon className="size-5" aria-hidden="true" />
                </span>
                <span className="text-sm font-semibold text-white">{title}</span>
                <span className="-mt-1.5 text-xs text-muted">{caption}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative mx-auto w-full max-w-[560px]">
          <BrushStrokes className="absolute left-1/2 top-1/2 -z-10 w-[118%] -translate-x-1/2 -translate-y-1/2" />
          <div data-hero="image">
            <div data-hero="float">
              {/* Bordas em degradê: o fundo preto da foto se funde ao da página e revela as pinceladas. */}
              <img
                src={bannerImg}
                alt="Hambúrguer artesanal com queijo derretido, alface e tomate no pão com gergelim"
                width={600}
                height={400}
                className="aspect-[3/2] w-full scale-110 object-cover [mask-image:radial-gradient(ellipse_52%_66%_at_58%_52%,#000_50%,transparent_88%)]"
              />
            </div>
          </div>

          <div data-hero="seal" className="absolute -bottom-6 left-0 sm:-bottom-4 sm:left-2">
            <FreshSeal />
          </div>

          <p
            data-hero="note"
            className="absolute -top-12 right-0 -rotate-6 font-script text-3xl text-white sm:-top-14 sm:text-4xl"
          >
            Selado na chapa!
            <svg viewBox="0 0 200 20" aria-hidden="true" className="mt-0.5 h-3 w-full">
              <path d="M4 14C60 4 140 2 196 10" stroke="#FFC107" strokeWidth="4" strokeLinecap="round" fill="none" />
            </svg>
          </p>
        </div>
      </div>

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} src={SITE.heroVideoUrl} />
    </section>
  );
};
